import { useState, useCallback } from "react";
import { CURRENT_CONVERSATION_IDENTIFIER } from "@/constants";

export interface ThreadIdPersistenceResult {
  threadId: string | null;
  setThreadId: (id: string | null, options?: { clearMessages?: () => void }) => void;
}

/**
 * Manage thread ID persistence with sessionStorage synchronization.
 * Single responsibility: Store and retrieve thread ID from sessionStorage.
 *
 * @returns Object with threadId state and setThreadId setter function
 */
export function useThreadIdPersistence(): ThreadIdPersistenceResult {
  // Use sessionStorage to persist threadId within browser tab session
  const [threadId, setThreadIdState] = useState<string | null>(() => {
    // Initialize from sessionStorage on mount
    if (typeof window !== "undefined") {
      return sessionStorage.getItem(CURRENT_CONVERSATION_IDENTIFIER);
    }
    return null;
  });

  // setThreadId function - updates state and sessionStorage
  const setThreadId = useCallback(
    (
      id: string | null,
      options?: { clearMessages?: () => void },
    ) => {
      // Clear messages when switching between two existing threads
      // DON'T clear when going from null to a thread ID (new thread creation)
      // This triggers the history loading mechanism in useStreamWithBackend
      if (id !== threadId && threadId !== null && options?.clearMessages) {
        options.clearMessages();
      }

      setThreadIdState(id);
      if (typeof window !== "undefined") {
        if (id) {
          sessionStorage.setItem(CURRENT_CONVERSATION_IDENTIFIER, id);
        } else {
          sessionStorage.removeItem(CURRENT_CONVERSATION_IDENTIFIER);
        }
      }
    },
    [threadId],
  );

  return { threadId, setThreadId };
}
